import { useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import AccountPage from "./AccountPage";
import ContactInfoPage from "./ContactInfoPage";
import DoctorDashboard from "./doctor/DoctorDashboard";
import { DrawerProvider } from "./DrawerContext";
import Header from "./Header";
import { useInactivityTimer } from "./hooks/useInactivityTimer";
import "./index.css";
import LifestylePage from "./LifestylePage";
import LoginPage from "./LoginPage";
import AddSubscription from "./patient/AddSubscription";
import AllergiesPage from "./patient/AllergiesPage";
import ConditionsPage from "./patient/ConditionsPage";
import DocumentsPage from "./patient/DocumentsPage";
import FindDoctorPage from "./patient/FindDoctorPage";
import HistoryPage from "./patient/HistoryPage";
import HospitalPage from "./patient/HospitalPage";
import MedicationsPage from "./patient/MedicationsPage";
import { PatientDashboard } from "./patient/PatientDashboard";
import ProceduresPage from "./patient/ProceduresPage";
import SalMineCardPage from "./patient/SalMineCardPage";
import SubscriptionHistory from "./patient/SubscriptionHistory";
import ViewPatientDetails from "./patient/ViewPatientDetails";
import ProfilePage from "./ProfilePage";
import SignupPage from "./SignupPage";
import HomePage from "./HomePage";
import ResetPasswordPage from "./ResetPasswordPage";
import AboutPage from "./AboutPage";
import TermsAndConditions from "./TermsAndConditions";
import { isAuthenticated } from "./utils/auth";
import { SubscriptionAccessProvider } from "./context/SubscriptionAccessContext";
import AddPaymentCard from "./patient/AddPaymentCard";
import { SettingsDashboard } from "./patient/SettingsDashboard";

function AppRoutes({ lang, setLang }) {
  const location = useLocation();

  // Enable inactivity timer only when user is authenticated and not on login/signup pages
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/" ||
    location.pathname === "/signup";
  const shouldEnableTimer = isAuthenticated() && !isAuthPage;

  // Initialize inactivity timer (15 minutes)
  useInactivityTimer(15, shouldEnableTimer);
  const isHomePage = location.pathname === "/" || location.pathname === "/about";
  const isSignup = location.pathname === "/signup";
  const isLogin = location.pathname === "/login";
  const isResetPassword = location.pathname === "/reset-password";
  const isTerms = location.pathname === "/terms-and-conditions";
  const isDoctorDashboard = location.pathname === "/doctor-dashboard";
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {!isHomePage && (isSignup || isLogin || isTerms || isResetPassword ? (
        <Header currentLang={lang} onLangChange={setLang} minimal />
      ) : (
        <Header
          currentLang={lang}
          onLangChange={setLang}
          portalType={isDoctorDashboard ? "doctor" : "patient"}
        />
      ))}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/signup" element={<SignupPage lang={lang} />} />
        <Route path="/login" element={<LoginPage lang={lang} />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/patient-dashboard"
          element={<PatientDashboard lang={lang} />}
        />
        <Route
          path="/settings-dashboard"
          element={<SettingsDashboard lang={lang} />}
        />
        <Route
          path="/doctor-dashboard"
          element={<DoctorDashboard lang={lang} />}
        />
        <Route path="/account" element={<AccountPage lang={lang} />} />
        <Route path="/contact-info" element={<ContactInfoPage lang={lang} />} />
        <Route path="/profile" element={<ProfilePage lang={lang} />} />
        <Route path="/lifestyle" element={<LifestylePage lang={lang} />} />
        <Route path="/find-doctor" element={<FindDoctorPage lang={lang} />} />
        <Route path="/hospitals" element={<HospitalPage lang={lang} />} />
        <Route path="/documents" element={<DocumentsPage lang={lang} />} />
        <Route path="/medications" element={<MedicationsPage lang={lang} />} />
        <Route path="/conditions" element={<ConditionsPage lang={lang} />} />
        <Route path="/procedures" element={<ProceduresPage lang={lang} />} />
        <Route path="/allergies" element={<AllergiesPage lang={lang} />} />
        <Route path="/history" element={<HistoryPage lang={lang} />} />
        <Route path="/sal-card" element={<SalMineCardPage lang={lang} />} />
        <Route path="/add-card" element={<AddPaymentCard lang={lang} />} />
        <Route
          path="/subscription-history"
          element={<SubscriptionHistory lang={lang} />}
        />
        <Route
          path="/view-patient-details"
          element={<ViewPatientDetails lang={lang} />}
        />
        <Route
          path="/add-subscription"
          element={<AddSubscription lang={lang} />}
        />
      </Routes>
    </div>
  );
}

function App() {
  const [lang, setLang] = useState("en");
  return (
    <BrowserRouter>
      <SubscriptionAccessProvider>
        <DrawerProvider>
          <AppRoutes lang={lang} setLang={setLang} />
          <ToastContainer position="top-right" />
        </DrawerProvider>
      </SubscriptionAccessProvider>
    </BrowserRouter>
  );
}

export default App;
